// Ensure a signed-in user has a Firestore profile (users/{uid}).
//
// This is the ONE place a teacher profile + provisional school + sample
// classroom get created, so every entry path is consistent:
//   • Google sign-in (pre-verified) — called from the Google buttons.
//   • Email/password — called from UserContext once the address is VERIFIED,
//     so bots that never click the link never generate any Firestore data.
//
// The Google button on the LOGIN page used to sign a user in without ever
// creating their profile doc — so a brand-new Google user ended up
// authenticated but PROFILE-LESS (no schoolId → every school-scoped read
// denied, school-join silently failing). This helper backfills a proper
// profile the same way registration does.
import { collection, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "./FirebaseConfig";
import { seedSampleClassroom } from "./sampleData";

// Keep in step with RegistrationForm's TERMS_VERSION / LegalPages "Last updated".
const TERMS_VERSION = "2026-06-12";

// Registration collects title / first name / last name that Firebase Auth can't
// carry across the email-verification gap (displayName only holds "Ms. Lastname").
// We stash them locally at sign-up and consume them when the verified profile is
// finally provisioned, so the teacher's real name survives verification.
const PENDING_KEY = "cb_pending_registration";

export function stashPendingRegistration(payload) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
  } catch {
    /* private mode / storage disabled — provisioning falls back to displayName */
  }
}

export function readPendingRegistration() {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
  } catch {
    return null;
  }
}

export function clearPendingRegistration() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* no-op */
  }
}

// Every user gets a real, unique provisional school from the moment they exist,
// even solo teachers ("an individual is a school of one"); joining a real school
// later replaces it. Hidden from school pickers via `provisional: true`.
async function createProvisionalSchool(uid, label) {
  const schoolRef = doc(collection(firestore, "schools"));
  await setDoc(schoolRef, {
    schoolName: label,
    provisional: true,
    createdBy: uid,
    teachers: [uid],
  });
  return schoolRef.id;
}

// Returns { created } — true when a fresh profile was provisioned (caller can
// route into onboarding), false when the user already had one. `extras` carries
// the registration fields (title/firstName/lastName) when available; otherwise
// they're parsed from the display name (the Google case).
export async function ensureUserProfile(user, extras = {}) {
  if (!user?.uid) return { created: false };

  const userRef = doc(collection(firestore, "users"), user.uid);
  const snap = await getDoc(userRef);
  if (snap.exists()) return { created: false };

  const [parsedFirst, ...parsedRest] = (user.displayName || "").split(" ");
  const firstName = extras.firstName || parsedFirst || "";
  const lastName = extras.lastName || parsedRest.join(" ") || "";
  const title = extras.title || "";
  const displayName =
    user.displayName ||
    [title && title !== "Other" ? title : "", lastName].filter(Boolean).join(" ").trim() ||
    "";

  const schoolName = `${displayName || "My"}'s Class`;
  const schoolId = await createProvisionalSchool(user.uid, schoolName);

  await setDoc(userRef, {
    email: user.email || "",
    uid: user.uid,
    displayName,
    firstName,
    lastName,
    title,
    role: "teacher",
    schoolId,
    schoolName,
    acceptedTermsAt: serverTimestamp(),
    termsVersion: TERMS_VERSION,
    onboarding: { complete: false, startedAt: serverTimestamp() },
  });

  // Seed a fully-alive demo classroom so they can try everything immediately.
  // Best-effort — a seeding hiccup must never block first entry.
  try {
    await seedSampleClassroom(user.uid, schoolId, displayName || "Teacher");
  } catch (seedErr) {
    console.error("Sample classroom seeding failed (non-blocking):", seedErr);
  }

  return { created: true };
}
